import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';
import { confirmDialog } from 'primereact/confirmdialog';
import { formatDateTimePanama } from '../utils/agendaDates';
import { mergeSalesSettings, WEEKDAY_OPTIONS } from '../utils/salesSettings';

const APPLY_OPTIONS = [
    { label: 'Aplicar el próximo lunes', value: 'nextMonday' },
    { label: 'Aplicar inmediatamente', value: 'immediate' }
];
const REPORT_LOCK_OPTIONS = [
    { label: 'Domingo', value: 7 },
    { label: 'Lunes siguiente', value: 8 }
];
const REQUIRED_FIELD_OPTIONS = [
    { label: 'Resumen', value: 'summary' },
    { label: 'Resultado', value: 'outcome' },
    { label: 'Persona con la que se reunió', value: 'metWith' }
];
const OUTCOME_OPTIONS = [
    { label: 'Sin cambios', value: 'noChange' },
    { label: 'Requiere seguimiento', value: 'followUpRequired' },
    { label: 'Solicitó cotización', value: 'quoteRequested' },
    { label: 'Solicitó muestra', value: 'sampleRequested' },
    { label: 'Oportunidad identificada', value: 'opportunityIdentified' },
    { label: 'Pedido potencial', value: 'potentialOrder' },
    { label: 'Queja', value: 'complaint' },
    { label: 'No interesado', value: 'notInterested' },
    { label: 'Visita no completada', value: 'visitNotCompleted' }
];
const ROLE_LABELS = {
    seller: 'Vendedor',
    sales: 'Supervisor de ventas',
    admin: 'Administrador',
    superAdmin: 'Superadministrador'
};
const PERMISSION_LABELS = {
    viewTeamAgenda: 'Ver agenda del equipo',
    manageSellerAgendas: 'Gestionar agendas de vendedores',
    reviewAgendas: 'Revisar agendas',
    manageSellers: 'Gestionar vendedores',
    viewAllLocations: 'Ver todas las ubicaciones',
    viewAllReports: 'Ver todos los reportes',
    manageSettings: 'Modificar ajustes',
    managePermissions: 'Modificar permisos'
};
const PROTECTED_ROLE_PERMISSIONS = new Set([
    'seller:viewTeamAgenda',
    'seller:manageSettings',
    'seller:managePermissions',
    'sales:viewTeamAgenda',
    'sales:manageSettings',
    'sales:managePermissions',
    'admin:viewTeamAgenda',
    'admin:manageSettings',
    'admin:managePermissions',
    'admin:viewAllLocations',
    'admin:viewAllReports'
]);

const SettingsSection = ({ title, description, children }) => (
    <div className="surface-card border-round p-3 shadow-1 ventas-settings-section">
        <h3 className="mt-0 mb-1">{title}</h3>
        {description ? <p className="text-color-secondary mt-0">{description}</p> : null}
        <div className="grid p-fluid">{children}</div>
    </div>
);

const ToggleField = ({ label, checked, onChange, disabled = false, helper }) => (
    <div className="col-12 md:col-6">
        <div className="flex align-items-center justify-content-between gap-3 border-1 surface-border border-round p-3 h-full ventas-settings-toggle">
            <div>
                <div className="font-medium">{label}</div>
                {helper ? <small className="text-color-secondary">{helper}</small> : null}
            </div>
            <InputSwitch checked={Boolean(checked)} onChange={(event) => onChange(event.value)} disabled={disabled} />
        </div>
    </div>
);

const SalesSettingsView = ({
    settingsState,
    loading,
    saving,
    onReload,
    onSave,
    onReset
}) => {
    const [form, setForm] = useState(() => mergeSalesSettings());
    const [applyMode, setApplyMode] = useState('nextMonday');
    const [comment, setComment] = useState('');

    useEffect(() => {
        if (settingsState?.activeConfig) {
            setForm(mergeSalesSettings(settingsState.activeConfig));
        }
    }, [settingsState?.activeConfig]);

    const updateSection = (section, field, value) => {
        setForm((current) => ({
            ...current,
            [section]: {
                ...current[section],
                [field]: value
            }
        }));
    };

    const updatePermission = (role, permission, value) => {
        setForm((current) => ({
            ...current,
            permissions: {
                ...current.permissions,
                [role]: {
                    ...current.permissions?.[role],
                    [permission]: value
                }
            }
        }));
    };

    const validationMessage = useMemo(() => {
        if (!settingsState?.activeConfig || !settingsState?.version) {
            return 'Recarga los ajustes antes de guardar.';
        }
        const numericValues = [
            form.planning.weeksAhead,
            form.planning.minVisitDurationMinutes,
            form.planning.maxVisitDurationMinutes,
            form.location.accuracyThresholdMeters,
            form.location.gpsTimeoutMs,
            form.location.minimumExceptionNoteLength,
            form.execution.longRunningVisitMinutes,
            form.reports.maxPhotos,
            form.reports.maxPhotoBytes,
            form.reports.maxTotalPhotoBytes
        ];
        if (numericValues.some((value) => !Number.isFinite(value))) {
            return 'Completa todos los límites numéricos.';
        }
        if (!form.planning.planningWeekdays.length) return 'Selecciona al menos un día de planificación.';
        if (!form.planning.commercialWeekdays.length) return 'Selecciona al menos un día comercial.';
        if (Math.max(...form.planning.planningWeekdays) > form.planning.submissionDeadlineWeekday) {
            return 'Los días de planificación no pueden quedar después del día límite.';
        }
        if (form.planning.minVisitDurationMinutes > form.planning.maxVisitDurationMinutes) {
            return 'La duración mínima no puede superar la máxima.';
        }
        if (!form.reports.requiredFields.includes('outcome')) {
            return 'El resultado debe permanecer como campo obligatorio.';
        }
        if (form.reports.maxTotalPhotoBytes < form.reports.maxPhotoBytes) {
            return 'El límite total de imágenes debe ser mayor que el límite por imagen.';
        }
        return '';
    }, [form, settingsState?.activeConfig, settingsState?.version]);

    const submit = () => {
        if (validationMessage || saving) return;
        const execute = () => onSave({
            version: settingsState.version,
            config: form,
            applyMode,
            comment: comment.trim() || null
        });
        if (applyMode === 'immediate') {
            confirmDialog({
                header: '¿Aplicar ajustes inmediatamente?',
                message: 'El cambio puede afectar agendas, ubicaciones o reportes que estén en curso.',
                acceptLabel: 'Aplicar ahora',
                rejectLabel: 'Cancelar',
                acceptClassName: 'p-button-warning',
                accept: execute
            });
            return;
        }
        execute();
    };

    const reset = () => {
        confirmDialog({
            header: '¿Restaurar valores predeterminados?',
            message: 'Se reemplazará toda la configuración operativa y de permisos.',
            acceptLabel: 'Restaurar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: () => onReset({
                version: settingsState.version,
                applyMode,
                comment: comment.trim() || 'Restauración de valores predeterminados'
            })
        });
    };

    if (loading && !settingsState?.activeConfig) {
        return <div className="surface-card border-round p-4 shadow-1 text-center">Cargando ajustes...</div>;
    }

    return (
        <div className="flex flex-column gap-3">
            <div className="surface-card border-round p-3 shadow-1">
                <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                    <div>
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <h2 className="m-0">Ajustes de Ventas</h2>
                            <Tag value={`Versión ${settingsState?.version || 1}`} severity="info" />
                        </div>
                        <p className="text-color-secondary mb-0">
                            Zona horaria: America/Panama · Última actualización: {formatDateTimePanama(settingsState?.updatedAt)}
                        </p>
                    </div>
                    <Button type="button" label="Recargar" icon="pi pi-refresh" outlined onClick={onReload} loading={loading} />
                </div>
                {settingsState?.pendingConfig ? (
                    <div className="surface-100 border-round p-3 mt-3">
                        <strong>Cambio programado:</strong> se activará el {formatDateTimePanama(settingsState.pendingEffectiveAt)}.
                    </div>
                ) : null}
            </div>

            <SettingsSection title="Planificación" description="Todas las semanas futuras están disponibles. Estos valores solo definen la semana inicial y la entrega sugerida.">
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Días sugeridos para preparar la agenda</label>
                    <MultiSelect value={form.planning.planningWeekdays} options={WEEKDAY_OPTIONS} onChange={(event) => updateSection('planning', 'planningWeekdays', event.value)} display="chip" />
                </div>
                <div className="col-12 md:col-3">
                    <label className="block font-medium mb-2">Día sugerido de entrega</label>
                    <Dropdown value={form.planning.submissionDeadlineWeekday} options={WEEKDAY_OPTIONS} onChange={(event) => updateSection('planning', 'submissionDeadlineWeekday', event.value)} />
                </div>
                <div className="col-12 md:col-3">
                    <label className="block font-medium mb-2">Hora sugerida de entrega</label>
                    <InputText type="time" value={form.planning.submissionDeadlineTime} onChange={(event) => updateSection('planning', 'submissionDeadlineTime', event.target.value)} />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Días comerciales</label>
                    <MultiSelect value={form.planning.commercialWeekdays} options={WEEKDAY_OPTIONS} onChange={(event) => updateSection('planning', 'commercialWeekdays', event.value)} display="chip" />
                </div>
                <div className="col-12 md:col-2">
                    <label className="block font-medium mb-2">Semana inicial sugerida</label>
                    <InputNumber value={form.planning.weeksAhead} min={1} max={8} onValueChange={(event) => updateSection('planning', 'weeksAhead', event.value)} />
                    <small className="text-color-secondary">No limita cuántas semanas hacia el futuro puedes planificar.</small>
                </div>
                <div className="col-12 md:col-2">
                    <label className="block font-medium mb-2">Duración mínima</label>
                    <InputNumber value={form.planning.minVisitDurationMinutes} suffix=" min" min={1} max={1439} onValueChange={(event) => updateSection('planning', 'minVisitDurationMinutes', event.value)} />
                </div>
                <div className="col-12 md:col-2">
                    <label className="block font-medium mb-2">Duración máxima</label>
                    <InputNumber value={form.planning.maxVisitDurationMinutes} suffix=" min" min={1} max={1439} onValueChange={(event) => updateSection('planning', 'maxVisitDurationMinutes', event.value)} />
                </div>
                <ToggleField label="Ajustes administrativos en la semana actual" checked={form.planning.adminOverride} onChange={(value) => updateSection('planning', 'adminOverride', value)} helper="Permite que administradores gestionen la semana en curso." />
            </SettingsSection>

            <SettingsSection title="Ubicación y ejecución" description="Define la evidencia GPS y las alertas operativas.">
                <ToggleField label="Exigir ubicación al iniciar" checked={form.location.requireStartLocation} onChange={(value) => updateSection('location', 'requireStartLocation', value)} />
                <ToggleField label="Exigir ubicación al finalizar" checked={form.location.requireEndLocation} onChange={(value) => updateSection('location', 'requireEndLocation', value)} />
                <ToggleField label="Permitir excepción con justificación" checked={form.location.allowWithoutLocationWithJustification} onChange={(value) => updateSection('location', 'allowWithoutLocationWithJustification', value)} />
                <ToggleField label="Una sola visita activa" checked={form.execution.enforceSingleActiveVisit} onChange={() => {}} disabled helper="Protección obligatoria contra visitas duplicadas." />
                <div className="col-12 md:col-3">
                    <label className="block font-medium mb-2">Precisión aceptada</label>
                    <InputNumber value={form.location.accuracyThresholdMeters} suffix=" m" min={10} max={5000} onValueChange={(event) => updateSection('location', 'accuracyThresholdMeters', event.value)} />
                </div>
                <div className="col-12 md:col-3">
                    <label className="block font-medium mb-2">Espera del GPS</label>
                    <InputNumber value={Math.round(form.location.gpsTimeoutMs / 1000)} suffix=" s" min={3} max={60} onValueChange={(event) => updateSection('location', 'gpsTimeoutMs', event.value === null ? null : event.value * 1000)} />
                </div>
                <div className="col-12 md:col-3">
                    <label className="block font-medium mb-2">Justificación mínima</label>
                    <InputNumber value={form.location.minimumExceptionNoteLength} suffix=" caracteres" min={3} max={100} onValueChange={(event) => updateSection('location', 'minimumExceptionNoteLength', event.value)} />
                </div>
                <div className="col-12 md:col-3">
                    <label className="block font-medium mb-2">Alerta de visita larga</label>
                    <InputNumber value={form.execution.longRunningVisitMinutes} suffix=" min" min={15} max={1440} onValueChange={(event) => updateSection('execution', 'longRunningVisitMinutes', event.value)} />
                </div>
            </SettingsSection>

            <SettingsSection title="Reportes" description="Controla cierre, edición, campos obligatorios y evidencia fotográfica.">
                <div className="col-12 md:col-3">
                    <label className="block font-medium mb-2">Día de cierre</label>
                    <Dropdown value={form.reports.lockWeekday} options={REPORT_LOCK_OPTIONS} onChange={(event) => updateSection('reports', 'lockWeekday', event.value)} />
                </div>
                <div className="col-12 md:col-3">
                    <label className="block font-medium mb-2">Hora de cierre</label>
                    <InputText type="time" value={form.reports.lockTime} onChange={(event) => updateSection('reports', 'lockTime', event.target.value)} />
                </div>
                <div className="col-12 md:col-2">
                    <label className="block font-medium mb-2">Máximo de fotos</label>
                    <InputNumber value={form.reports.maxPhotos} min={0} max={20} onValueChange={(event) => updateSection('reports', 'maxPhotos', event.value)} />
                </div>
                <div className="col-12 md:col-2">
                    <label className="block font-medium mb-2">KB por foto</label>
                    <InputNumber value={Math.round(form.reports.maxPhotoBytes / 1024)} min={100} max={2048} onValueChange={(event) => updateSection('reports', 'maxPhotoBytes', event.value === null ? null : event.value * 1024)} />
                </div>
                <div className="col-12 md:col-2">
                    <label className="block font-medium mb-2">Total de MB</label>
                    <InputNumber value={Number((form.reports.maxTotalPhotoBytes / 1024 / 1024).toFixed(1))} minFractionDigits={1} maxFractionDigits={1} min={0.1} max={8} onValueChange={(event) => updateSection('reports', 'maxTotalPhotoBytes', event.value === null ? null : Math.round(event.value * 1024 * 1024))} />
                </div>
                <ToggleField label="Editar reportes enviados" checked={form.reports.allowSubmittedEditsUntilLock} onChange={(value) => updateSection('reports', 'allowSubmittedEditsUntilLock', value)} helper="Permite cambios hasta la fecha de cierre." />
                <div className="col-12 md:col-6">
                    <label className="block font-medium mb-2">Campos obligatorios</label>
                    <MultiSelect
                        value={form.reports.requiredFields}
                        options={REQUIRED_FIELD_OPTIONS}
                        onChange={(event) => {
                            const values = event.value || [];
                            updateSection(
                                'reports',
                                'requiredFields',
                                values.includes('outcome') ? values : [...values, 'outcome']
                            );
                        }}
                        display="chip"
                    />
                    <small className="text-color-secondary">El resultado siempre es obligatorio porque define si la visita fue completada.</small>
                </div>
                <div className="col-12">
                    <label className="block font-medium mb-2">Resultados que exigen seguimiento</label>
                    <MultiSelect value={form.reports.followUpRequiredOutcomes} options={OUTCOME_OPTIONS} onChange={(event) => updateSection('reports', 'followUpRequiredOutcomes', event.value)} display="chip" />
                </div>
            </SettingsSection>

            <SettingsSection title="Permisos por rol" description={settingsState?.canManagePermissions ? 'Los cambios afectan a todos los usuarios del rol.' : 'Solo el superadministrador puede modificar esta matriz.'}>
                <div className="col-12 ventas-permissions-accordion">
                    <Accordion>
                        {Object.entries(form.permissions || {}).map(([role, permissions]) => (
                            <AccordionTab header={ROLE_LABELS[role] || role} key={role}>
                            <div className="grid">
                                {Object.entries(PERMISSION_LABELS).map(([permission, label]) => (
                                    <ToggleField
                                        key={permission}
                                        label={label}
                                        checked={permissions?.[permission]}
                                        onChange={(value) => updatePermission(role, permission, value)}
                                        disabled={
                                            !settingsState?.canManagePermissions ||
                                            role === 'superAdmin' ||
                                            PROTECTED_ROLE_PERMISSIONS.has(`${role}:${permission}`)
                                        }
                                    />
                                ))}
                            </div>
                            </AccordionTab>
                        ))}
                    </Accordion>
                </div>
            </SettingsSection>

            <div className="surface-card border-round p-3 shadow-1">
                <h3 className="mt-0">Guardar cambios</h3>
                <div className="grid p-fluid align-items-end">
                    <div className="col-12 md:col-4">
                        <label className="block font-medium mb-2">Cuándo aplicar</label>
                        <Dropdown value={applyMode} options={APPLY_OPTIONS} onChange={(event) => setApplyMode(event.value)} />
                    </div>
                    <div className="col-12 md:col-5">
                        <label className="block font-medium mb-2">Motivo del cambio</label>
                        <InputText value={comment} maxLength={500} onChange={(event) => setComment(event.target.value)} placeholder="Opcional" />
                    </div>
                    <div className="col-12 md:col-3 flex gap-2 ventas-settings-actions">
                        <Button type="button" label="Guardar ajustes" icon="pi pi-save" onClick={submit} loading={saving} disabled={Boolean(validationMessage) || saving} />
                        {settingsState?.canManagePermissions ? <Button type="button" icon="pi pi-undo" severity="danger" outlined onClick={reset} disabled={saving} tooltip="Restaurar valores predeterminados" /> : null}
                    </div>
                </div>
                {validationMessage ? <small className="p-error">{validationMessage}</small> : null}
            </div>

            <div className="surface-card border-round p-3 shadow-1">
                <h3 className="mt-0">Historial reciente</h3>
                {settingsState?.history?.length ? (
                    <div className="flex flex-column gap-2">
                        {settingsState.history.map((item, index) => (
                            <div className="border-bottom-1 surface-border pb-2" key={`${item.changedAt}-${index}`}>
                                <div className="font-medium">{item.changedBy?.name || item.changedBy?.email || 'Usuario administrativo'}</div>
                                <div className="text-sm text-color-secondary">
                                    {formatDateTimePanama(item.changedAt)} · {item.applyMode === 'immediate' ? 'Aplicación inmediata' : `Programado para ${formatDateTimePanama(item.effectiveAt)}`}
                                </div>
                                {item.comment ? <div className="text-sm mt-1">{item.comment}</div> : null}
                            </div>
                        ))}
                    </div>
                ) : <span className="text-color-secondary">Todavía no hay cambios registrados.</span>}
            </div>
        </div>
    );
};

export default SalesSettingsView;
